import React from 'react';
import '../../../styles/precision-enhancements.css';

/**
 * ForensicsTab Component
 * Displays chain of custody, provenance logs, and legal transparency data
 * Only visible for precision-enhanced targets
 */
const ForensicsTab = ({ target }) => {
  // Only render for precision-enhanced targets
  if (!target.precision_enhanced || !target.vi_verified) {
    return (
      <div className="forensics-tab">
        <div className="forensics-section">
          <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '40px 20px' }}>
            Forensics data is only available for precision-enhanced scans.
          </p>
        </div>
      </div>
    );
  }

  const provenance = target.provenance_log || {};
  const humanReadable = target.human_readable_summary || {};
  const trajectory = target.trajectory || {};
  const boundingBoxes = target.bounding_boxes || [];
  const videoContinuity = target.video_continuity || {};
  const multiVehicleMatching = target.multi_vehicle_matching || null;
  const labelDetection = target.label_detection || null;

  // Session 7 Hybrid Model: Legal Report Stats
  const legalStats = target.legal_report_stats || {};
  const appearanceSegments = target.appearance_segments || [];
  const hasLegalStats = legalStats.total_plate_detections > 0 || legalStats.verified_windows > 0;

  return (
    <div className="forensics-tab">
      {/* Legal Report Stats (Session 7 Hybrid Model) - Primary Evidence */}
      {hasLegalStats && (
        <div className="forensics-section" style={{ 
          background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(34, 197, 94, 0.05) 100%)', 
          borderColor: '#22c55e',
          borderWidth: '2px'
        }}>
          <h4>📋 Legal Evidence Report</h4>
          <p className="section-note" style={{ marginBottom: '16px', fontSize: '13px' }}>
            Court-ready evidence summary. All plate detections verified by Google's Video Intelligence OCR.
          </p>
          
          {/* Key Stats Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '12px',
            marginBottom: '16px'
          }}>
            {legalStats.total_plate_detections > 0 && (
              <div style={{
                background: 'rgba(34, 197, 94, 0.15)',
                borderRadius: '8px',
                padding: '12px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#22c55e' }}>
                  {legalStats.total_plate_detections}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Verified Plate Reads
                </div>
              </div>
            )}
            
            {legalStats.verified_windows > 0 && (
              <div style={{
                background: 'rgba(59, 130, 246, 0.15)',
                borderRadius: '8px',
                padding: '12px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#3b82f6' }}>
                  {legalStats.verified_windows}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Verified Windows
                </div>
              </div>
            )}
            
            {legalStats.verified_duration > 0 && (
              <div style={{
                background: 'rgba(139, 92, 246, 0.15)',
                borderRadius: '8px',
                padding: '12px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#8b5cf6' }}>
                  {legalStats.verified_duration.toFixed(1)}s
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Verified Duration
                </div>
              </div>
            )}
            
            {legalStats.confidence_avg && (
              <div style={{
                background: 'rgba(245, 158, 11, 0.15)',
                borderRadius: '8px',
                padding: '12px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f59e0b' }}>
                  {(legalStats.confidence_avg * 100).toFixed(0)}%
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Avg Confidence
                </div>
              </div>
            )}
          </div>
          
          {/* Confidence Range */}
          {legalStats.confidence_min && legalStats.confidence_max && (
            <div style={{
              padding: '8px 12px',
              background: 'rgba(0,0,0,0.2)',
              borderRadius: '4px',
              fontSize: '12px',
              marginBottom: '12px'
            }}>
              <span style={{ color: 'var(--text-secondary)' }}>Confidence Range: </span>
              <span style={{ color: 'var(--text-primary)', fontWeight: '600' }}>
                {(legalStats.confidence_min * 100).toFixed(0)}% - {(legalStats.confidence_max * 100).toFixed(0)}%
              </span>
            </div>
          )}
          
          {/* Tracked Windows (if any) */}
          {legalStats.tracked_windows > 0 && (
            <div style={{
              padding: '8px 12px',
              background: 'rgba(245, 158, 11, 0.1)',
              borderRadius: '4px',
              fontSize: '12px',
              border: '1px solid rgba(245, 158, 11, 0.3)'
            }}>
              <span style={{ color: '#f59e0b' }}>○ </span>
              <span style={{ color: 'var(--text-secondary)' }}>
                Additional {legalStats.tracked_windows} tracked window(s) 
                {legalStats.tracked_duration > 0 && ` (${legalStats.tracked_duration.toFixed(1)}s)`}
                {' '}- vehicle visible but plate not readable
              </span>
            </div>
          )}
          
          {/* Legal Statement */}
          <div style={{
            marginTop: '16px',
            padding: '12px',
            background: 'rgba(34, 197, 94, 0.1)',
            borderRadius: '6px',
            borderLeft: '3px solid #22c55e'
          }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#22c55e', marginBottom: '4px' }}>
              ✓ Suitable for Court Testimony
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
              License plate "{target.attributes?.license_plate || 'N/A'}" was verified by Google's 
              Video Intelligence API at {legalStats.total_plate_detections || 0} distinct timestamps 
              with confidence scores ranging from {legalStats.confidence_min ? `${(legalStats.confidence_min * 100).toFixed(0)}%` : 'N/A'} to {legalStats.confidence_max ? `${(legalStats.confidence_max * 100).toFixed(0)}%` : 'N/A'}.
            </div>
          </div>
        </div>
      )}

      {/* Chain of Custody */}
      {provenance && Object.keys(provenance).length > 0 && (
        <div className="forensics-section">
          <h4>🔗 Chain of Custody</h4>
          
          {/* Gemini Claim - Full width (descriptive text) */}
          {provenance.gemini_claim && (
            <div className="custody-claim">
              <span className="custody-label">Gemini Claim</span>
              <p className="custody-claim-text">{provenance.gemini_claim}</p>
            </div>
          )}
          
          {/* Two-column grid for compact data */}
          <div className="custody-grid">
            {/* Plates Row */}
            {provenance.gemini_plate && (
              <div className="custody-item">
                <span className="custody-label">Gemini Plate</span>
                <span className="custody-value mono">{provenance.gemini_plate}</span>
              </div>
            )}
            {provenance.vi_plate && (
              <div className="custody-item">
                <span className="custody-label">VI Plate</span>
                <span className="custody-value mono">{provenance.vi_plate}</span>
              </div>
            )}
            
            {/* Verification Row */}
            {provenance.vi_confirmation && (
              <div className="custody-item">
                <span className="custody-label">VI Confirmation</span>
                <span className="custody-value mono">{provenance.vi_confirmation}</span>
              </div>
            )}
            {provenance.anchor_type && (
              <div className="custody-item">
                <span className="custody-label">Anchor Type</span>
                <span className={`anchor-badge ${provenance.anchor_type}`}>
                  {provenance.anchor_type}
                </span>
              </div>
            )}
            
            {/* Score & Timestamp Row */}
            {provenance.match_score !== undefined && (
              <div className="custody-item">
                <span className="custody-label">Match Score</span>
                <span className="custody-value highlight">{(provenance.match_score * 100).toFixed(1)}%</span>
              </div>
            )}
            {provenance.timestamp && (
              <div className="custody-item">
                <span className="custody-label">Verified At</span>
                <span className="custody-value">{new Date(provenance.timestamp).toLocaleString()}</span>
              </div>
            )}
            
            {/* Label detected (if present) */}
            {provenance.label_detected && (
              <div className="custody-item">
                <span className="custody-label">Label Detected</span>
                <span className="custody-value">{provenance.label_detected}</span>
              </div>
            )}
          </div>
          
          {/* Methodology - Full width (descriptive) */}
          {provenance.methodology && (
            <div className="custody-methodology">
              <span className="custody-label">Methodology</span>
              <p className="custody-methodology-text">{provenance.methodology}</p>
            </div>
          )}
        </div>
      )}

      {/* Label Detection Info (for TERTIARY anchors - parked vehicles) */}
      {labelDetection && (
        <div className="forensics-section" style={{ background: 'rgba(139, 92, 246, 0.05)', borderColor: '#8b5cf6' }}>
          <h4>🏷️ Label Detection (Parked Vehicle)</h4>
          <p className="section-note" style={{ marginBottom: '12px' }}>
            {labelDetection.description || 'Vehicle verified via VI LABEL_DETECTION.'}
          </p>
          <div className="attribute-grid">
            <div className="attribute-item">
              <span className="attribute-label">Label</span>
              <span className="attribute-value" style={{ color: '#8b5cf6', fontWeight: 600 }}>
                {labelDetection.label}
              </span>
            </div>
            <div className="attribute-item">
              <span className="attribute-label">Confidence</span>
              <span className="attribute-value">
                {labelDetection.confidence ? `${(labelDetection.confidence * 100).toFixed(0)}%` : 'N/A'}
              </span>
            </div>
          </div>
          {labelDetection.note && (
            <p className="section-note" style={{ marginTop: '12px', fontSize: '12px' }}>
              ℹ️ {labelDetection.note}
            </p>
          )}
        </div>
      )}

      {/* Human-Readable Summary */}
      {humanReadable && Object.keys(humanReadable).length > 0 && (
        <div className="forensics-section">
          <h4>📋 Plain Language Summary</h4>
          <div className="readable-summary">
            {humanReadable.what && (
              <p><strong>What:</strong> {humanReadable.what}</p>
            )}
            {humanReadable.when && (
              <p><strong>When:</strong> {humanReadable.when}</p>
            )}
            {humanReadable.how_long && (
              <p><strong>Duration:</strong> {humanReadable.how_long}</p>
            )}
            {humanReadable.confidence_explanation && (
              <p><strong>Confidence:</strong> {humanReadable.confidence_explanation}</p>
            )}
            {humanReadable.verification_status && (
              <p><strong>Verification:</strong> {humanReadable.verification_status}</p>
            )}
          </div>
        </div>
      )}

      {/* Video Continuity Report */}
      {videoContinuity && Object.keys(videoContinuity).length > 0 && (
        <div className="forensics-section">
          <h4>📊 Video Continuity</h4>
          <div className="attribute-grid">
            <div className="attribute-item">
              <span className="attribute-label">Status</span>
              <span className="attribute-value">
                {videoContinuity.is_continuous ? '✓ CONTINUOUS' : '⚠️ EDITED'}
              </span>
            </div>
            <div className="attribute-item">
              <span className="attribute-label">Shot Count</span>
              <span className="attribute-value">{videoContinuity.shot_count || 0}</span>
            </div>
            {videoContinuity.cuts_detected_at && videoContinuity.cuts_detected_at.length > 0 && (
              <div className="attribute-item full-width">
                <span className="attribute-label">Cuts Detected At</span>
                <span className="attribute-value">
                  {videoContinuity.cuts_detected_at.join(', ')}
                </span>
              </div>
            )}
          </div>
          {videoContinuity.is_continuous ? (
            <p className="section-note" style={{ marginTop: '12px' }}>
              This video has not been edited or spliced. It represents a continuous recording.
            </p>
          ) : (
            <p className="section-note" style={{ marginTop: '12px', borderLeftColor: '#f59e0b', background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}>
              ⚠️ This video contains {videoContinuity.shot_count} distinct shots, indicating editing or multiple takes.
            </p>
          )}
        </div>
      )}

      {/* Tracking Data */}
      {boundingBoxes && boundingBoxes.length > 0 && (
        <div className="forensics-section">
          <h4>📊 Tracking Data</h4>
          <p className="section-note">
            Video Intelligence tracked this target across {boundingBoxes.length} frames with deterministic bounding boxes.
          </p>
          <div className="bounding-boxes-preview">
            <div className="bbox-stat">
              <span className="stat-label">First Frame</span>
              <span className="stat-value">{boundingBoxes[0]?.time || 'N/A'}</span>
            </div>
            <div className="bbox-stat">
              <span className="stat-label">Last Frame</span>
              <span className="stat-value">{boundingBoxes[boundingBoxes.length - 1]?.time || 'N/A'}</span>
            </div>
            <div className="bbox-stat">
              <span className="stat-label">Total Frames</span>
              <span className="stat-value">{boundingBoxes.length}</span>
            </div>
          </div>
        </div>
      )}

      {/* Multi-Vehicle Matching (NEW - Option C) */}
      {multiVehicleMatching && multiVehicleMatching.score !== undefined && (
        <div className="forensics-section" style={{ background: 'rgba(34, 197, 94, 0.05)', borderColor: '#22c55e' }}>
          <h4>🎯 Multi-Vehicle Matching</h4>
          <p className="section-note" style={{ marginBottom: '12px' }}>
            {multiVehicleMatching.description || 'Vehicle track selected using plate containment scoring.'}
          </p>
          <div className="attribute-grid">
            <div className="attribute-item">
              <span className="attribute-label">Match Score</span>
              <span className="attribute-value" style={{ color: '#22c55e', fontWeight: 600 }}>
                {(multiVehicleMatching.score * 100).toFixed(1)}%
              </span>
            </div>
            {multiVehicleMatching.breakdown && (
              <>
                <div className="attribute-item">
                  <span className="attribute-label">Containment</span>
                  <span className="attribute-value">
                    {multiVehicleMatching.breakdown.containment != null 
                      ? `${(multiVehicleMatching.breakdown.containment * 100).toFixed(0)}%` 
                      : 'N/A'}
                  </span>
                </div>
                <div className="attribute-item">
                  <span className="attribute-label">Spatial IoU</span>
                  <span className="attribute-value">
                    {multiVehicleMatching.breakdown.iou != null 
                      ? `${(multiVehicleMatching.breakdown.iou * 100).toFixed(0)}%` 
                      : 'N/A'}
                  </span>
                </div>
                <div className="attribute-item">
                  <span className="attribute-label">Temporal</span>
                  <span className="attribute-value">
                    {multiVehicleMatching.breakdown.temporal_coverage != null 
                      ? `${(multiVehicleMatching.breakdown.temporal_coverage * 100).toFixed(0)}%` 
                      : 'N/A'}
                  </span>
                </div>
                {multiVehicleMatching.breakdown.frames_analyzed && (
                  <div className="attribute-item">
                    <span className="attribute-label">Frames Analyzed</span>
                    <span className="attribute-value">{multiVehicleMatching.breakdown.frames_analyzed}</span>
                  </div>
                )}
              </>
            )}
          </div>
          <p className="section-note" style={{ marginTop: '12px', fontSize: '12px' }}>
            ✓ Bounding box verified: License plate position was inside this vehicle's bounding box.
          </p>
        </div>
      )}

      {/* Movement Analysis */}
      {trajectory && Object.keys(trajectory).length > 0 && (
        <div className="forensics-section">
          <h4>🧭 Movement Analysis</h4>
          <div className="trajectory-info">
            {trajectory.direction_degrees !== undefined && (
              <div className="trajectory-item">
                <span className="traj-label">Direction:</span>
                <span className="traj-value">
                  {trajectory.direction_degrees}° 
                  {trajectory.direction_label && ` (${trajectory.direction_label})`}
                </span>
              </div>
            )}
            {trajectory.start_position && (
              <div className="trajectory-item">
                <span className="traj-label">Start Position:</span>
                <span className="traj-value">
                  x: {trajectory.start_position.x?.toFixed(3) || 'N/A'}, 
                  y: {trajectory.start_position.y?.toFixed(3) || 'N/A'}
                </span>
              </div>
            )}
            {trajectory.end_position && (
              <div className="trajectory-item">
                <span className="traj-label">End Position:</span>
                <span className="traj-value">
                  x: {trajectory.end_position.x?.toFixed(3) || 'N/A'}, 
                  y: {trajectory.end_position.y?.toFixed(3) || 'N/A'}
                </span>
              </div>
            )}
          </div>
          <p className="section-note" style={{ marginTop: '12px' }}>
            Note: Coordinates are normalized (0.0 to 1.0) relative to video frame dimensions.
          </p>
        </div>
      )}

      {/* Legal Note */}
      <div className="forensics-section" style={{ background: 'rgba(59, 130, 246, 0.05)', borderColor: '#3b82f6' }}>
        <h4>💡 Legal Note</h4>
        <p style={{ fontSize: '14px', lineHeight: '1.6', color: 'var(--text-secondary)' }}>
          This tab provides documentation for expert testimony and court admissibility. 
          All data shown here is backed by Google's deterministic Video Intelligence API, 
          not generated by AI. The provenance log demonstrates the methodology used to 
          cross-verify AI findings with mathematical measurements, suitable for forensic analysis.
        </p>
      </div>

      {/* No Data Available */}
      {!provenance?.gemini_claim && !humanReadable?.what && !videoContinuity?.shot_count && boundingBoxes.length === 0 && !trajectory?.direction_degrees && (
        <div className="forensics-section">
          <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '40px 20px' }}>
            No forensics data available for this target.
          </p>
        </div>
      )}
    </div>
  );
};

export default ForensicsTab;

